import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ComingUpRead } from '../../../lib/coming-up';
import { addDays, startOfDay, toDayParam } from '../../../lib/calendar/calendar-dates';
import { itemTitle } from '../../../lib/calendar/item-text';
import { useCalendarItems } from '../../../lib/calendar/use-calendar-items';
import { useCalendarTitlePreference } from '../../../lib/calendar/use-calendar-title-preference';
import { useTranslation } from '../../../lib/i18n';
import type { Home3ComingUpProps } from './Home3ComingUp';

export interface ComingUpCalendar {
  items: Home3ComingUpProps['calendarItems'];
  meetings: ComingUpRead;
  bookings: ComingUpRead;
}

/**
 * Coming up's meetings and bookings for the next eight business days. Mounted only for an account in the
 * Calendar release, so an account outside it makes none of these reads.
 */
export function ComingUpCalendarReads({ timeZone, children }: { timeZone: string; children: (calendar: ComingUpCalendar) => ReactNode }) {
  const { t } = useTranslation();
  const [today, setToday] = useState(() => startOfDay(Date.now(), timeZone));
  useEffect(() => {
    const now = startOfDay(Date.now(), timeZone);
    setToday(now);
    const timer = setTimeout(() => setToday(addDays(now, 1, timeZone)), addDays(now, 1, timeZone) - Date.now());
    return () => clearTimeout(timer);
  }, [timeZone, today]);
  const range = useMemo(() => ({ from: today, to: addDays(today, 8, timeZone) }), [today, timeZone]);
  const titlePreference = useCalendarTitlePreference();
  const calendar = useCalendarItems(range, undefined, { live: true, showEventTitles: titlePreference.showEventTitles });

  const read = (kind: 'meeting' | 'booking'): ComingUpRead => {
    const source = calendar.sources.find((entry) => entry.kind === kind);
    if (!source || source.loading) return 'loading';
    return source.completeness === 'complete' ? 'ready' : 'error';
  };
  const items = useMemo(
    () =>
      calendar.items
        .filter((item) => item.kind === 'meeting' || item.kind === 'booking')
        .map((item) => ({
          key: item.key,
          kind: item.kind as 'meeting' | 'booking',
          title: itemTitle(item, t),
          start: item.start,
          end: item.end,
          day: toDayParam(item.start, timeZone),
          sourceLabel: item.calendar?.name ?? item.sourceLabel,
          allDay: item.allDay,
        })),
    [calendar.items, timeZone, t],
  );
  return <>{children({ items, meetings: read('meeting'), bookings: read('booking') })}</>;
}
